import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useState } from "react";
import { useNotifications } from "@/context/NotificationsContext";
import { useTheme } from "@/context/ThemeContext";
import { useAuth } from "@/context/AuthContext";
import { useLocationShare } from "@/context/LocationContext";
import { cn } from "@/lib/utils";
import { LayoutDashboard, Truck, MessageSquare, User, LayoutGrid, Bell, Loader2, X, Sun, Moon, Eye, MapPin } from "lucide-react";

const TABS = [
  { to: "/", label: "Home", icon: LayoutDashboard, end: true },
  { to: "/transports", label: "Calls", icon: Truck },
  { to: "/chat", label: "Chat", icon: MessageSquare, badge: "chat" },
  { to: "/profile", label: "Profile", icon: User },
  { to: "/more", label: "More", icon: LayoutGrid },
];

export function AppShell({ children }) {
  const { unreadChat, permission, requestPermission } = useNotifications();
  const { resolved, toggle } = useTheme();
  const { isAdmin, viewAsDriver, setViewAsDriver } = useAuth();
  const { enabled: locEnabled, permission: locPerm, requestLocation } = useLocationShare();
  const navigate = useNavigate();
  const location = useLocation();
  const [dismissed, setDismissed] = useState(() => localStorage.getItem("curtron_notif_prompt_dismissed") === "1");
  const [locDismissed, setLocDismissed] = useState(() => localStorage.getItem("curtron_loc_prompt_dismissed") === "1");

  const showNotifBanner = permission === "default" && !dismissed;
  const showLocBanner = locEnabled && locPerm === "prompt" && !locDismissed;
  const dismissBanner = () => { localStorage.setItem("curtron_notif_prompt_dismissed", "1"); setDismissed(true); };
  const enableNotifs = async () => { await requestPermission(); setDismissed(true); };

  return (
    <div className="h-screen w-full bg-curtron-bg flex flex-col">
      {/* Top bar */}
      <header className="shrink-0 h-16 bg-curtron-surface border-b border-curtron-line flex items-center gap-3 px-4 sm:px-6 lg:px-8 z-20">
        <button onClick={() => navigate("/")} className="flex items-center gap-3" data-testid="topbar-logo">
          <img src="/curtron-emblem.png" alt="Curtron" className="h-6 w-auto dark:brightness-0 dark:invert" />
          <div className="leading-tight text-left border-l border-curtron-line pl-3">
            <div className="font-heading font-bold text-curtron-ink text-sm">Curtron Hub</div>
            <div className="text-[9px] uppercase tracking-[0.14em] text-curtron-muted">Transport Ops</div>
          </div>
        </button>
        <button onClick={toggle} data-testid="theme-toggle-btn" aria-label="Toggle dark mode"
          className="ml-auto p-2 rounded-md hover:bg-curtron-bg transition-colors text-curtron-sub">
          {resolved === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
        </button>
        {isAdmin && !viewAsDriver && (
          <button onClick={() => { setViewAsDriver(true); navigate("/"); }} data-testid="view-as-driver-btn"
            className="hidden sm:inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-curtron-line text-curtron-sub text-sm font-medium hover:border-curtron-green hover:text-curtron-green transition-colors">
            <Eye className="w-4 h-4" /> View as Driver
          </button>
        )}
        <button onClick={() => navigate("/chat")} data-testid="topbar-notification-btn" className="relative p-2 rounded-md hover:bg-curtron-bg transition-colors">
          <Bell className="w-5 h-5 text-curtron-sub" />
          {unreadChat > 0 && <span className="absolute top-1 right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-curtron-terracotta text-white text-[10px] leading-4 text-center">{unreadChat}</span>}
        </button>
      </header>

      {/* Driver-view banner */}
      {isAdmin && viewAsDriver && (
        <div className="shrink-0 bg-curtron-ochre/15 border-b border-curtron-ochre/30 px-4 sm:px-6 lg:px-8 py-2 flex items-center gap-3" data-testid="driver-view-banner">
          <Eye className="w-4 h-4 text-curtron-ochre shrink-0" />
          <p className="text-sm text-curtron-ink flex-1 min-w-0 font-medium">You're viewing as a <span className="text-curtron-ochre">Driver</span> — showing only calls assigned to you.</p>
          <button onClick={() => setViewAsDriver(false)} data-testid="exit-driver-view-btn"
            className="shrink-0 h-8 px-3 rounded-md bg-curtron-ochre text-white text-sm font-medium hover:opacity-90 transition-opacity">
            Exit driver view
          </button>
        </div>
      )}

      {/* Enable-location prompt — lets dispatch see the driver on the fleet map */}
      {showLocBanner && (
        <div className="shrink-0 bg-curtron-info/10 border-b border-curtron-info/20 px-4 sm:px-6 lg:px-8 py-2.5 flex items-center gap-3" data-testid="location-prompt-banner">
          <MapPin className="w-4 h-4 text-curtron-info shrink-0" />
          <p className="text-sm text-curtron-ink flex-1 min-w-0">Share your location so dispatch can see you on the live fleet map.</p>
          <button onClick={requestLocation} data-testid="location-prompt-enable"
            className="shrink-0 h-8 px-3 rounded-md bg-curtron-info text-white text-sm font-medium hover:opacity-90 transition-opacity">
            Share location
          </button>
          <button onClick={() => { localStorage.setItem("curtron_loc_prompt_dismissed", "1"); setLocDismissed(true); }} data-testid="location-prompt-dismiss" className="shrink-0 p-1 rounded-md text-curtron-muted hover:bg-curtron-line/40 transition-colors" aria-label="Dismiss">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Enable-notifications prompt */}
      {showNotifBanner && (
        <div className="shrink-0 bg-curtron-green/10 border-b border-curtron-green/20 px-4 sm:px-6 lg:px-8 py-2.5 flex items-center gap-3" data-testid="notif-prompt-banner">
          <Bell className="w-4 h-4 text-curtron-green shrink-0" />
          <p className="text-sm text-curtron-ink flex-1 min-w-0">
            Turn on alerts for new dispatch calls, messages &amp; announcements.
          </p>
          <button onClick={enableNotifs} data-testid="notif-prompt-enable"
            className="shrink-0 h-8 px-3 rounded-md bg-curtron-green text-white text-sm font-medium hover:bg-curtron-green/90 transition-colors">
            Enable
          </button>
          <button onClick={dismissBanner} data-testid="notif-prompt-dismiss" className="shrink-0 p-1 rounded-md text-curtron-muted hover:bg-white/60 transition-colors" aria-label="Dismiss">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Scrollable content */}
      <main className="flex-1 overflow-y-auto min-h-0">
        <div className="max-w-6xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-6 animate-fade-in" key={location.pathname}>{children}</div>
      </main>

      {/* Full-width bottom tab bar */}
      <nav className="shrink-0 bg-curtron-surface border-t border-curtron-line flex items-stretch z-20" data-testid="bottom-tab-bar">
        {TABS.map((t) => (
          <NavLink key={t.to} to={t.to} end={t.end} data-testid={`tab-nav-${t.label.toLowerCase()}`}
            className={({ isActive }) => cn(
              "flex-1 flex flex-col items-center justify-center gap-0.5 py-2.5 relative transition-colors",
              isActive ? "text-curtron-green" : "text-curtron-muted hover:text-curtron-sub"
            )}>
            <span className="relative">
              <t.icon className="w-[22px] h-[22px]" strokeWidth={2} />
              {t.badge === "chat" && unreadChat > 0 && <span className="absolute -top-1 -right-2 min-w-[15px] h-[15px] px-1 rounded-full bg-curtron-terracotta text-white text-[9px] leading-[15px] text-center">{unreadChat}</span>}
            </span>
            <span className="text-[11px] font-medium">{t.label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}

export function PageLoader() {
  return (
    <div className="flex items-center justify-center py-24">
      <Loader2 className="w-6 h-6 animate-spin text-curtron-green" />
    </div>
  );
}
